import { plural } from '@lingui/core/macro'
import { useLingui } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import { Text } from 'designSystem/components/Text'
import { ChevronDown, Pencil, Trash2 } from 'lucide-react'
import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { MOBILE_LAYOUT_QUERY } from 'utils/mobileLayout'

import type { ActiveModel } from './modelCard'
import type { AgentModel } from './useAgentModels'

/** Choosing which saved model is on the chart, and acting on one. */

const Wrap = styled.div`
  position: relative;
  flex: 0 1 auto;
  min-width: 0;
`

const Trigger = styled.button`
  all: unset;
  cursor: pointer;
  /* Deliberately not flex:1 — the bar also carries the KPI-only toggle and the
     view's state, and a full-width name pushed them off the edge. */
  max-width: 11rem;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.375rem;
  border: 1px solid ${({ theme }) => theme.forecast.cardDividerBackground};
  border-radius: 0.375rem;
  padding: 0.25rem 0.5rem;
  &:hover {
    background: ${({ theme }) => theme.forecast.hoverGhostButton};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.forecast.primary};
    outline-offset: 1px;
  }
`

const Menu = styled.div`
  position: absolute;
  top: calc(100% + 0.25rem);
  left: 0;
  min-width: min(20rem, calc(100vw - 2rem));
  max-width: calc(100vw - 2rem);
  z-index: 20;
  max-height: 18rem;
  overflow-y: auto;
  background: ${({ theme }) => theme.forecast.cardBackground};
  border: 1px solid ${({ theme }) => theme.forecast.cardDividerBackground};
  border-radius: 0.375rem;
  box-shadow: 0 0.5rem 1.5rem ${({ theme }) => theme.forecast.boxShadow};
  padding: 0.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`

const Search = styled.input`
  width: 100%;
  background: transparent;
  color: inherit;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 0.25rem;
  padding: 0.25rem 0.375rem;
  font: inherit;
  margin-bottom: 0.125rem;

  @media ${MOBILE_LAYOUT_QUERY} {
    font-size: 1rem;
  }
`

const Row = styled.div<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.375rem;
  border-radius: 0.25rem;
  background: ${({ $selected, theme }) =>
    $selected ? theme.forecast.activeGhostButton : 'transparent'};
  &:hover {
    background: ${({ theme }) => theme.forecast.hoverGhostButton};
  }
`

const Pick = styled.button`
  all: unset;
  cursor: pointer;
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.forecast.primary};
    outline-offset: 1px;
  }
`

const Stat = styled(Text)<{ $bad?: boolean }>`
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: ${({ $bad, theme }) => ($bad ? theme.forecast.dangerText : 'inherit')};
`

const Note = styled(Text)`
  display: block;
  padding: 0.5rem 0.375rem;
`

/** Below this many models the filter box is more chrome than help. */
const FILTER_THRESHOLD = 6

const fmt = (value?: number): string =>
  typeof value === 'number' ? String(Math.round(value * 1000) / 1000) : '—'

export const ModelPicker: React.FC<{
  models: AgentModel[]
  active: ActiveModel | null
  onView: (model: AgentModel) => void
  onOpenDetail: (model: AgentModel) => void
  onDelete: (model: AgentModel) => void
}> = ({ models, active, onView, onOpenDetail, onDelete }) => {
  const { t } = useLingui()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const wrapRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const away = (event: MouseEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', away)
    return () => document.removeEventListener('mousedown', away)
  }, [open])

  const needle = query.trim().toLowerCase()
  const visible = needle
    ? models.filter(
        (model) =>
          (model.name ?? '').toLowerCase().includes(needle) ||
          (model.kpi ?? '').toLowerCase().includes(needle)
      )
    : models

  const label = active
    ? (active.name ?? active.kpi ?? t`model`)
    : models.length
      ? plural(models.length, {
          one: '# saved model',
          other: '# saved models',
        })
      : t`no models yet`

  return (
    <Wrap ref={wrapRef}>
      <Trigger
        type='button'
        aria-expanded={open}
        aria-haspopup='menu'
        onClick={() => setOpen((prev) => !prev)}>
        <Text variant='caption' truncate>
          {label}
        </Text>
        <ChevronDown size={13} />
      </Trigger>
      {open && (
        <Menu role='menu'>
          {models.length > FILTER_THRESHOLD && (
            <Search
              autoFocus
              aria-label={t`Filter models`}
              placeholder={t`Filter by name or KPI…`}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          )}
          {!models.length && (
            <Note variant='caption' color='muted'>
              {t`No saved models yet. Ask the agent to build one, then say “save it”.`}
            </Note>
          )}
          {visible.map((model) => {
            const modelName = model.name ?? model.entry_id
            return (
              <Row
                key={model.entry_id}
                $selected={active?.entry_id === model.entry_id}>
                <Pick
                  type='button'
                  title={t`Draw this model on the chart`}
                  onClick={() => {
                    setOpen(false)
                    onView(model)
                  }}>
                  <Text variant='caption' truncate>
                    {model.name ?? model.entry_id}
                  </Text>
                  <Stat
                    variant='caption'
                    color={(model.last30_sigma ?? 0) < -2 ? 'inherit' : 'muted'}
                    $bad={(model.last30_sigma ?? 0) < -2}>
                    {model.kpi} · {fmt(model.last30_gap)} (
                    {fmt(model.last30_sigma)}σ)
                  </Stat>
                </Pick>
                <Button
                  variant='ghostMuted'
                  size='icon-sm'
                  aria-label={t`Open ${modelName} model card`}
                  title={t`Open the model card — every parameter the agent chose`}
                  onClick={() => {
                    setOpen(false)
                    onOpenDetail(model)
                  }}>
                  <Pencil />
                </Button>
                <Button
                  variant='ghostMuted'
                  size='icon-sm'
                  aria-label={t`Delete ${modelName}`}
                  title={t`Delete this model`}
                  onClick={() => {
                    if (
                      window.confirm(
                        t`Delete "${modelName}"? This removes the saved model and its fit series.`
                      )
                    ) {
                      onDelete(model)
                    }
                  }}>
                  <Trash2 />
                </Button>
              </Row>
            )
          })}
          {active?.unsaved && (
            <Note variant='caption' color='muted'>
              {t`The model on the chart is not saved yet, so it is not in this list. Open its card to name and save it.`}
            </Note>
          )}
        </Menu>
      )}
    </Wrap>
  )
}
